import EventEmitter from 'eventemitter3';
import { RecordingError } from '../Errors';
import { getSecondsElapsed } from '@/functions';
import { EventNames } from '@/constants';

const {
  CONNECTED,
  CONNECTING_TIME_REPORT,
  ERROR,
  PROGRESS_UPDATE,
  STOPPING_TIME_REPORT,
  UPLOAD_SPEED_UPDATE,
  UPLOAD_SUCCESS,
} = EventNames.RecordingStrategy;

/**
 * A RecordingStrategy has an API of
 * 1. prepareForRecording() - Called before recording starts
 * 2. createUploader - Called to create the strategy's uploader
 * 3. handleNewDataEvent(event) - Called when a new data event is received
 * 4. finishUpload() - Called to finish the strategy's upload
 * 5. done() - Called when recording is done
 * 6. dispose() - Called when recording is ready to be cleaned up
 * 7. emitUnknownError() - Called when an unknown error occurs for that recording strategy
 * 7. get strategyName - The name of the strategy
 *
 * Emits these events:
 * 1. recordingStrategy:connected - When the socket is connected
 * 2. recordingStrategy:progressUpdate - When the upload progress is updated
 * 3. recordingStrategy:uploadSpeedUpdate - When the upload speed is updated
 * 4. recordingStrategy:uploadSuccess - When the upload is successfully completed
 * 5. recordingStrategy:error - When an unrecoverable error occurs
 * 6. recordingStrategy:connectingTimeReport - When the upload strategy is connected and ready to
 *      record, emits the number of seconds it took to connect
 * 7. recordingStrategy:stoppingTimeReport - When upload is complete, emits the number of seconds
 *      it took to finish stopping the upload
 *
 *  See the VideoUploader constants file for the event names under EventNames.RecordingStrategy.
 */
export abstract class RecordingStrategyBase extends EventEmitter {
  finishConnectingTime = 0;

  beginStoppingTime = 0;

  finishStoppingTime = 0;

  totalVideoSizeInMB = 0;

  totalUploadSpeedInMbps = 0;

  estimatedVideoDuration = 0;

  firstProgressTime: PerformanceMark | null = null;

  constructor(
    protected videoId: string,
    protected containerFormat: string,
    protected beginConnectingTime: number,
  ) {
    super();
  }

  abstract get strategyName(): string;

  prepareForRecording() {
    try {
      this.createUploader();
    } catch (error) {
      this.emitUnknownError(error);
    }
  }

  abstract createUploader(): void;

  abstract handleNewDataEvent(event: BlobEvent): void;

  // eslint-disable-next-line class-methods-use-this
  finishUpload() {
    throw new Error('finishUpload not implemented');
  }

  done(estimatedVideoDuration: typeof RecordingStrategyBase.prototype.estimatedVideoDuration) {
    try {
      this.estimatedVideoDuration = estimatedVideoDuration;
      this.beginStoppingTime = Date.now();
      this.finishUpload();
    } catch (error) {
      this.emitUnknownError(error);
    }
  }

  abstract dispose(): void;

  emitConnected() {
    this.finishConnectingTime = Date.now();
    const connectingSecondsElapsed = getSecondsElapsed(
      this.beginConnectingTime,
      this.finishConnectingTime,
    );
    const { videoId, containerFormat, strategyName: recordingStrategyName } = this;
    const connectingTimeReport = {
      videoId,
      recordingStrategyName,
      containerFormat,
      connectingSecondsElapsed,
    };
    this.emit(CONNECTING_TIME_REPORT, connectingTimeReport);
    this.emit(CONNECTED);
  }

  emitProgressUpdate(progress: number) {
    this.emit(PROGRESS_UPDATE, progress);
  }

  emitUploadSpeedUpdate(uploadSpeedInMbps: number) {
    this.emit(UPLOAD_SPEED_UPDATE, uploadSpeedInMbps);
  }

  emitUploadSuccess(videoUrl: string) {
    this.finishStoppingTime = Date.now();
    let finishTime = { duration: -1 };
    if (this.firstProgressTime) {
      finishTime = performance.measure('upload-finish', {
        start: this.firstProgressTime.startTime,
      });
    }
    const stoppingSecondsElapsed = getSecondsElapsed(
      this.beginStoppingTime,
      this.finishStoppingTime,
    );
    const {
      videoId,
      containerFormat,
      strategyName: recordingStrategyName,
      totalVideoSizeInMB,
      totalUploadSpeedInMbps,
      estimatedVideoDuration,
    } = this;
    const stoppingTimeReport = {
      videoId,
      recordingStrategyName,
      containerFormat,
      stoppingSecondsElapsed,
      totalVideoSizeInMB,
      totalUploadSpeedInMbps,
      estimatedVideoDuration,
      totalDataTimeInMS: finishTime.duration !== -1 ? finishTime.duration : 'unknown',
    };
    this.emit(STOPPING_TIME_REPORT, stoppingTimeReport);

    this.emit(UPLOAD_SUCCESS, videoUrl);
    this.dispose();
  }

  /**
   * Log and emit a recording error, then dispose of the recording.
   */
  emitError(recordingError: RecordingError) {
    console.error(
      'RecordingStrategyBase: Error:',
      recordingError,
      'Original error:',
      recordingError.originalError,
    );
    this.emit(ERROR, recordingError);
    this.dispose();
  }

  abstract emitUnknownError(error: unknown): void;
}
